import { jsx } from 'react/jsx-runtime';
import { useToast } from '@flex-design-system/fx';
import { captureException } from '@flex-mf/sentry';
import { decideError, CustomErrorType, isNegligibleSentryCapture } from '@flexteam/error';
import { QueryClient, MutationCache, QueryCache, QueryClientProvider, Hydrate } from '@tanstack/react-query';
import { useState } from 'react';
import { handleApiError } from './handleApiError.mjs';

function shouldCaptureSentry(ignoreSentry, error) {
  const ignore = (() => {
    if (typeof ignoreSentry === "boolean") {
      return ignoreSentry;
    }
    if (typeof ignoreSentry === "function") {
      return ignoreSentry(error);
    }
    return false;
  })();
  if (ignore) {
    return false;
  }
  return !isNegligibleSentryCapture(error);
}
function isNegligibleError(ignoreGlobalOnError, error) {
  if (typeof ignoreGlobalOnError === "boolean") {
    return ignoreGlobalOnError;
  } else if (typeof ignoreGlobalOnError === "function") {
    return ignoreGlobalOnError(error);
  }
  return false;
}
const DEFAULT_MESSAGE = "\uC54C \uC218 \uC5C6\uB294 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4";
function ReactQueryProvider({
  children,
  dehydratedState,
  staleTime
}) {
  const toast = useToast();
  const [queryClient] = useState(() => {
    const defaultQueryClient = new QueryClient({
      queryCache: new QueryCache({
        onError: (error) => {
          handleApiError(error);
        }
      }),
      mutationCache: new MutationCache({
        onError: (error, __, ___, mutation) => {
          const { ignoreGlobalOnError, ignoreSentry } = mutation.meta ?? {};
          if (shouldCaptureSentry(ignoreSentry, error)) {
            captureException(error);
          }
          if (isNegligibleError(ignoreGlobalOnError, error)) {
            return;
          }
          const { isHandled, httpError } = handleApiError(error);
          if (!isHandled) {
            toast.error(
              /* @__PURE__ */ jsx("div", { style: { whiteSpace: "pre-line" }, children: httpError?.message ?? DEFAULT_MESSAGE })
            );
          }
        }
      }),
      defaultOptions: {
        queries: {
          staleTime,
          suspense: true,
          useErrorBoundary: true,
          refetchOnWindowFocus: false,
          networkMode: "offlineFirst",
          retry: (failureCount, error) => {
            if (failureCount > 3) {
              return false;
            }
            const [errorType] = decideError(error);
            return errorType === CustomErrorType.ServerError;
          }
        },
        mutations: {
          networkMode: "offlineFirst"
        }
      }
    });
    return defaultQueryClient;
  });
  return /* @__PURE__ */ jsx(QueryClientProvider, { client: queryClient, children: /* @__PURE__ */ jsx(Hydrate, { state: dehydratedState, children }) });
}

export { ReactQueryProvider };
//# sourceMappingURL=ReactQueryProvider.mjs.map
